import { useEffect, useMemo, useState, type ReactElement } from 'react';
import type { FileHandleRef } from '@slate/core';
import { Modal, type ModalAction } from './Modal';

export interface CloseAllWorkspacesDialogProps {
  open: boolean;
  roots: FileHandleRef[];
  /** Called with the IDs the user chose to close. */
  onConfirm: (ids: string[]) => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog for "Close all workspace folders" (Cmd/Ctrl+Shift+W).
 * Every workspace is checked by default; the user can uncheck individual rows
 * to keep them open. The action label updates to match the live selection
 * count, and the primary button disables when nothing is selected so an empty
 * confirm cannot accidentally fire.
 */
export function CloseAllWorkspacesDialog({
  open,
  roots,
  onConfirm,
  onCancel,
}: CloseAllWorkspacesDialogProps): ReactElement {
  const allIds = useMemo(() => roots.map((r) => r.id), [roots]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set(allIds));

  // Reset the selection every time the dialog opens or the roots list shifts —
  // re-opening after a cancel should bring back all-checked, not whatever was
  // left from last time.
  useEffect(() => {
    if (open) setSelected(new Set(allIds));
  }, [open, allIds]);

  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = selected.size;
  const title =
    count === 0
      ? 'Close workspace folders?'
      : count === 1
        ? 'Close workspace folder?'
        : `Close ${count} workspace folders?`;
  const confirmLabel = count <= 1 ? 'Close selected' : `Close ${count} folders`;

  // Cancel = 'c'; the affirmative "Close …" uses 'o' (the o in "Close") to avoid
  // colliding with Cancel's first letter. The confirm stays inert while nothing
  // is selected, mirroring the old disabled button so an empty confirm can't fire.
  const actions: ModalAction[] = [
    { id: 'cancel', label: 'Cancel', onSelect: onCancel, accelerator: 'c', isCancel: true },
    {
      id: 'confirm',
      label: confirmLabel,
      onSelect: () => {
        if (count > 0) onConfirm(Array.from(selected));
      },
      accelerator: 'o',
      variant: 'primary',
      isDefault: true,
    },
  ];

  return (
    <Modal open={open} onClose={onCancel} title={title} actions={actions}>
      <p className="mp-modal__text">
        Open files stay open. Uncheck a folder to keep it in the sidebar.
      </p>
      <ul className="mp-modal__list mp-modal__list--check" role="list">
        {roots.map((r) => {
          const checked = selected.has(r.id);
          return (
            <li key={r.id} className="mp-modal__list-item" title={r.path ?? r.name}>
              <label className="mp-modal__check">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(r.id)}
                />
                <span className="mp-modal__list-name">{r.name}</span>
                {r.path && <span className="mp-modal__list-path">{r.path}</span>}
              </label>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}
